import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { createDateAxis, createValueAxis } from "../TrendsUtils";
import type { AgeBySeverityTrendConfig } from "./ageBySeverityTrendConfig";
import Chart from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import useTrend from "../useTrend";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../../General/useSeverityAsPriority";
import { capitalize } from "lodash-es";
import type { SelectedScope } from "../../../../types/scope";

export interface AgeBySeverityTrendProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  timeScale: string;
  chartConfig: AgeBySeverityTrendConfig;
  id: string | number;
  widgetKey?: string;
  setIsReadyToExport?: (isReady: boolean) => void;
}

// A single point on a severity series, as accumulated from the raw trend rows.
interface SeriesPoint {
  date: string;
  value: number;
  dashLength?: number;
}

// dataObj keyed by severity value -> list of points.
type SeriesDataMap = Record<string, SeriesPoint[]>;

// Raw trend rows: [date, severity, value] tuples returned by useTrend.
type TrendRow = [string, string | number, number];

const AgeBySeverityTrend: React.FC<AgeBySeverityTrendProps> = ({
  selectedScope,
  filterToExecute,
  timeScale,
  scopeToExecute,
  chartConfig,
  id,
  widgetKey,
  setIsReadyToExport,
}) => {
  const { getSeverityField } = useSeverityAsPriority();
  const [max, setMax] = useState(0);
  const chartName = `${chartConfig.chartNamePrefix}-${id}`;
  const {
    trendData: data,
    trendDataError,
    trendLoading,
    refetchTrend,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      timescale: timeScale,
      selection: chartConfig.selection,
      group_by: ["severity"],
      aggregate_functions: ["AVG"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_exclude: chartConfig.filtersFieldsToExclude,
      },
      snapshots_filters_config: {
        filtersjson: filterToExecute,
        filters_fields_to_include: chartConfig.snapshotsFiltersFieldsToInclude,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });
  const [trendData, setTrendData] = useState<SeriesDataMap | null>(null);
  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(setIsReadyToExport ?? null);

  const clearData = () => {
    setTrendData(null);
  };

  useEffect(() => {
    if (!!trendLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [trendLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!trendDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [trendDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      // `data` is typed as unknown by useTrend; this widget consumes it as an
      // array of [date, severity, value] tuples.
      const rows = data as TrendRow[];
      const dataObj: SeriesDataMap = {};

      for (let i = 0; i < rows.length; i += 1) {
        const severity = String(rows[i][1]);
        if (!dataObj.hasOwnProperty(severity)) {
          dataObj[severity] = [];
        }

        dataObj[severity].push({
          date: rows[i][0],
          value: rows[i][2],
        });
      }

      if (
        !dataObj ||
        !Object.values(dataObj).find((obj) =>
          obj.find((item) => item.value !== 0)
        )
      ) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (Object.keys(dataObj)?.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      Object.keys(dataObj).forEach((item) => {
        const itemData = dataObj[item];
        if (itemData?.length >= 2) {
          itemData[itemData?.length - 2].dashLength = 8;
        }
      });

      let maxNum = 0;
      rows.forEach((item) => {
        item?.forEach((value) => {
          if (Number(value) > maxNum) {
            maxNum = Number(value);
          }
        });
      });

      setMax(maxNum * 1.2);
      setTrendData(dataObj);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart, legendContainer] = createChart(chartName, am4charts.XYChart);

      chart.cursor = new am4charts.XYCursor();

      chart.paddingTop = 45;
      chart.paddingBottom = -10;
      chart.paddingRight = 40;

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      createDateAxis(dateAxis, timeScale);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, chartConfig.valueAxisTitle, max);
      valueAxis.getSeriesDataItem = function (
        this: am4charts.ValueAxis,
        series,
        position
      ) {
        const key = this.axisFieldName + this.axisLetter;
        const value = this.positionToValue(position);
        const dataItem = series.dataItems.getIndex(
          series.dataItems.findClosestIndex(
            value,
            (x) => {
              const record = x as unknown as Record<string, number | undefined>;
              return record[key]
                ? record[key]!
                : (undefined as unknown as number);
            },
            "any"
          )
        );
        return dataItem;
      } as typeof valueAxis.getSeriesDataItem;

      let label = dateAxis.renderer.labels.template;
      label.wrap = true;
      label.maxWidth = 85;
      label.textAlign = "middle";

      if (trendData) {
        for (const [key] of Object.entries(trendData).sort(
          (a, b) => Number(b[0]) - Number(a[0])
        )) {
          const severityName = getSeverityField(key, SeverityFieldsEnum.NAME);
          if (!severityName) {
            continue;
          }
          createSeries(key, capitalize(severityName));
        }
      }

      function createSeries(s: string, name: string) {
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = "value";
        series.dataFields.dateX = "date";
        series.name = name;
        series.tensionX = 0.8;
        series.strokeWidth = 2;
        series.stroke = am4core.color(
          getSeverityField(s, SeverityFieldsEnum.COLOR)
        );
        series.fill = am4core.color(
          getSeverityField(s, SeverityFieldsEnum.COLOR)
        );
        series.strokeOpacity = 0.6;
        series.tooltipText = "{name}: {value}";
        series.tooltip!.autoTextColor = false;

        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.strokeWidth = 2;
        bullet.circle.radius = 3;

        let segment = series.segments.template;
        segment.interactionsEnabled = true;

        segment.events.on("over", function (event) {
          // segment -> segments container -> series; typed as Container at the
          // boundary but is the owning LineSeries at runtime.
          processOver(
            event.target.parent!.parent!
              .parent as unknown as am4charts.LineSeries
          );
        });

        segment.events.on("out", function (event) {
          processOut(
            event.target.parent!.parent!
              .parent as unknown as am4charts.LineSeries
          );
        });

        series.data = trendData![s];
        series.propertyFields.strokeDasharray = "dashLength";

        return series;
      }

      chart.legend.itemContainers.template.events.on("over", function (event) {
        processOver(event.target.dataItem!.dataContext as am4charts.LineSeries);
      });

      chart.legend.itemContainers.template.events.on("out", function (event) {
        processOut(event.target.dataItem!.dataContext as am4charts.LineSeries);
      });

      function processOver(hoveredSeries: am4charts.LineSeries) {
        hoveredSeries.toFront();

        hoveredSeries.segments.each(function (segment) {
          segment.setState("hover");
        });

        chart.series.each(function (series) {
          if (series !== hoveredSeries) {
            (series as am4charts.LineSeries).segments.each(function (segment) {
              segment.setState("dimmed");
            });
            series.bulletsContainer.setState("dimmed");
          }
        });
      }

      function processOut(hoveredSeries: am4charts.LineSeries) {
        chart.series.each(function (series) {
          (series as am4charts.LineSeries).segments.each(function (segment) {
            segment.setState("default");
          });
          series.bulletsContainer.setState("default");
        });
      }

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [trendData, dataExists, createChart]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={dataError}
      chartData={dataExists}
      chartReload={refetchTrend}
    />
  );
};

export default AgeBySeverityTrend;
